import { BillingUsageLimitsResponse, useBillingUsageLimits } from 'api/BillingApi';
import { addNegativeToastWithErrorId } from 'modules/toastContainer';
import { FC, PropsWithChildren, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useAllowPreview } from 'utils/hooks/useAllowPreview';
import { useFeatureFlag } from 'utils/hooks/useFeatureFlag';
import {
  CUSTOM_EVENTS_METRICS,
  JOURNEY_EVENTS_METRICS,
  MESSAGING_METRICS,
  SMS_METRICS,
  USAGE_LIMITS_FF,
  USAGE_LIMITS_PREVIEW_KEY,
  USERS_METRICS,
} from '../constants';
import { UsageLimitsContext } from './UsageLimitsContext';

/** List of metric names that have usage limits */
const metricNames = [
  /** custom events */
  CUSTOM_EVENTS_METRICS.totalCustomEvents,
  /** journey events */
  JOURNEY_EVENTS_METRICS.jvtCount,
  /** messaging */
  MESSAGING_METRICS.emailsSent,
  MESSAGING_METRICS.inAppNotificationsSent,
  MESSAGING_METRICS.pushNotificationsSent,
  MESSAGING_METRICS.webPushNotificationsSent,
  /** sms */
  SMS_METRICS.mmsAttachmentsSent,
  SMS_METRICS.smsSegmentsSent,
  /** users */
  USERS_METRICS.totalUsersAllTime,
];

export const UsageLimitsContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const dispatch = useDispatch();
  const isBillingLimitsPreviewEnabled = useAllowPreview(USAGE_LIMITS_PREVIEW_KEY);
  const isBillingLimitsFeatureFlagEnabled = useFeatureFlag(USAGE_LIMITS_FF);
  const isBillingLimitsEnabled = isBillingLimitsPreviewEnabled || isBillingLimitsFeatureFlagEnabled;

  const {
    data: { metricUsageLimit = {} } = {} as BillingUsageLimitsResponse,
    isLoading,
    mutate,
  } = useBillingUsageLimits({
    onError: (error: unknown) => {
      dispatch(
        addNegativeToastWithErrorId(
          'Unable to load usage billing limits',
          'usageLimitsError',
          error,
        ),
      );
    },
  });

  useEffect(() => {
    if (isBillingLimitsEnabled) {
      mutate({ metricNames });
    }
  }, [isBillingLimitsEnabled, mutate]);

  return (
    <UsageLimitsContext.Provider value={{ isBillingLimitsEnabled, isLoading, metricUsageLimit }}>
      {children}
    </UsageLimitsContext.Provider>
  );
};
